import { type RefObject, useLayoutEffect } from 'react';
import { chatSearchHighlightPattern } from '@/utils/chat-search-highlight';

function clearSearchHits(root: HTMLElement) {
  root.querySelectorAll('mark[data-search-hit]').forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    parent.replaceChild(document.createTextNode(mark.textContent || ''), mark);
    parent.normalize();
  });
}

function highlightSearchHits(root: HTMLElement, query: string): number {
  const pattern = chatSearchHighlightPattern(query, root.innerText || '');
  if (!pattern) return 0;

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (!(node instanceof Text) || !node.nodeValue?.trim()) continue;
    if (node.parentElement?.closest('mark[data-search-hit]')) continue;
    nodes.push(node);
  }

  let hitCount = 0;
  for (const textNode of nodes) {
    const text = textNode.nodeValue || '';
    pattern.lastIndex = 0;
    if (!pattern.test(text)) continue;
    pattern.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match = pattern.exec(text);
    while (match) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }
      const mark = document.createElement('mark');
      mark.setAttribute('data-search-hit', 'true');
      mark.className = 'rounded-sm bg-yellow-200 px-px text-inherit';
      mark.textContent = match[0];
      fragment.appendChild(mark);
      hitCount += 1;
      lastIndex = match.index + match[0].length;
      if (match[0].length === 0) break;
      match = pattern.exec(text);
    }
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    textNode.parentNode?.replaceChild(fragment, textNode);
  }
  return hitCount;
}

function scrollToCenter(container: HTMLElement, target: HTMLElement) {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  container.scrollTop = Math.max(
    0,
    container.scrollTop
      + (targetRect.top - containerRect.top)
      - containerRect.height / 2
      + targetRect.height / 2,
  );
}

function findMessageElement(messageId: number): HTMLElement | null {
  const byIds = document.querySelector(`[data-message-ids~="${messageId}"]`);
  if (byIds instanceof HTMLElement) return byIds;
  const byId = document.querySelector(`[data-message-id="${messageId}"]`);
  return byId instanceof HTMLElement ? byId : null;
}

export function useFocusChatMessage(
  focusMessage: { id: number; token: number; query?: string } | null,
  enabled: boolean,
  options?: {
    scrollRef?: RefObject<HTMLElement | null>;
    stopScroll?: () => void;
  },
) {
  useLayoutEffect(() => {
    if (!focusMessage || !enabled) return undefined;

    let cancelled = false;
    let attempts = 0;
    let highlightedRoot: HTMLElement | null = null;
    options?.stopScroll?.();

    const tryScroll = () => {
      if (cancelled) return;
      options?.stopScroll?.();
      const el = findMessageElement(focusMessage.id);
      if (el) {
        if (focusMessage.query) {
          clearSearchHits(el);
          const hits = highlightSearchHits(el, focusMessage.query);
          highlightedRoot = el;
          if (hits === 0 && attempts < 40) {
            attempts += 1;
            window.setTimeout(tryScroll, 50);
            return;
          }
        }
        const target = (el.querySelector('mark[data-search-hit]') as HTMLElement | null) || el;
        const container = options?.scrollRef?.current;
        if (container) {
          scrollToCenter(container, target);
        } else {
          target.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
        return;
      }
      attempts += 1;
      if (attempts < 40) {
        window.setTimeout(tryScroll, 50);
      }
    };

    const frame = window.requestAnimationFrame(tryScroll);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      if (highlightedRoot) {
        clearSearchHits(highlightedRoot);
      }
    };
  }, [enabled, focusMessage, options?.scrollRef, options?.stopScroll]);
}
